// @ts-strict-ignore
import type { Viewport } from 'react-flow-renderer';
import type { WorkflowState } from './state';
import type {
  Action,
  NodeEditorTab,
  PublicId,
  RuleBuilderSubEditorSide,
  SchemaInstance,
  Threshold,
  WorkflowProps,
} from '../engine/types';
import type { Delta } from 'jsondiffpatch';
import type {
  getDatarouteHtmlElementId,
  getMLAHtmlElementId,
  getMatrixValueHtmlElementId,
  getNodeEditorFormHtmlElementId,
} from 'flow/editor/hooks/useWorkflowHistory/helpers';
import type { IsolatedEditingBlockData } from 'flow/editor/components/NodeEditorDrawer/types';
import type { Operation } from 'flow/service/changelog/types';

// todo - think about moving this to the graph dir
export type CanvasRect = {
  x: Viewport['x'];
  y: Viewport['y'];
  zoom: Viewport['zoom'];
  width: DOMRect['width'];
  height: DOMRect['height'];
};

export type CanvasProperties = {
  isMoving: boolean;
  rect: CanvasRect;
};

export type NodeConnectorInteractionState = 'hovered' | 'focused';
export type NodeConnectorIntent = 'link' | 'add';

export type DerivedNodeConnectorState = WorkflowState['ui']['connector'] & {
  /**
   * this field is related to the connector state, but can be derived from the existing
   * store values, so we do not keep it in the store.
   */
  intent: NodeConnectorIntent;
};

export type Snapshot<T> = {
  snapshotId: PublicId;
  data: T;
};

export type WorkflowSnapshots = {
  workflowData: {
    history: {
      initial: Snapshot<Partial<SchemaInstance>>;
      past: Snapshot<Partial<SchemaInstance>>[];
    };
  };
  workflowProps: {
    history: {
      initial: Snapshot<WorkflowProps>;
    };
  };
};

export const snippetMode = {
  pastePending: 'pastePending',
  preview: 'preview',
  review: 'review',
} as const;

export type SnippetMode = keyof typeof snippetMode;

export const snippetOperation = {
  insertSnippet: 'insertSnippet',
  insertLinkedSnippet: 'insertLinkedSnippet',
  upgradeLinkedSnippet: 'upgradeLinkedSnippet',
  /** Not a snippet — an AI-proposed edit reusing review: applied, then kept or rolled back. */
  aiProposal: 'aiProposal',
} as const;

export type SnippetOperation = keyof typeof snippetOperation;

export type SnippetActiveOperation = {
  operation: SnippetOperation | null;
  snippetActionPublicId: PublicId | null;
};

export type WorkflowCopyPasteSupport = {
  supported: boolean;
  reason: string;
};

export const snippetInsertionErrorType = {
  tagNameTooLong: 'tagNameTooLong',
} as const;

export type SnippetInsertionErrorType = keyof typeof snippetInsertionErrorType;

export type SnippetInsertionError = {
  type: SnippetInsertionErrorType;
  actionPublicId: PublicId;
};

// generated by jsonDiffPatch
export type WorkflowDiff = {
  workflowData: Delta;
  workflowProps: Delta | null;
};

export type WorkflowStateSnapshot = {
  workflowData: Partial<SchemaInstance>;
  workflowProps: WorkflowProps;
};

// TBD add more descriptions
export const changeDescription = {
  action: 'action',
  expression: 'expression',
  group: 'group',
  inputConfiguration: 'inputConfiguration',
  serviceLocalSettings: 'serviceLocalSettings',
  matrixValue: 'matrixValue',
  matrixOption: 'matrixOption',
  mla: 'mla',
  outputJqSettings: 'outputJqSettings',
  outputOptions: 'outputOptions',
  aggregation: 'aggregation',
  threshold: 'threshold',
  dataroute: 'dataroute',
  snippet: 'snippet',
  globalSettings: 'globalSettings',
  tagLocalSettings: 'tagLocalSettings',
  failureHandlingMode: 'failureHandlingMode',
  sdkConfig: 'sdkConfig',
} as const;

export type ChangeDescription = keyof typeof changeDescription;

// ================ Ui Effect Context ================

/** Fields shared by every UI effect context, discriminated by `target`. */
type BaseUiEffectContext<TTarget extends ChangeDescription> = {
  target: TTarget;
};

export type ActionUiEffectContext = BaseUiEffectContext<typeof changeDescription.action> & {
  /** Only present when the history entry affects a single action that can be focused. */
  publicId?: Action['publicId'];
  nodeEditorTab?: NodeEditorTab;
};

export type ThresholdUiEffectContext = BaseUiEffectContext<typeof changeDescription.threshold> & {
  publicId: Threshold['publicId'];
  actionId: Threshold['actionId'];
};

export type GroupUiEffectContext = BaseUiEffectContext<typeof changeDescription.group> & {
  actionId: Action['publicId'];
};

export type ThresholdSubEditorArgs = {
  threshold: {
    publicId: Threshold['publicId'];
    side: RuleBuilderSubEditorSide;
  };
};

export type DataRouteSubEditorArgs = {
  dataRoute: IsolatedEditingBlockData;
};

export type MLAUiEffectContext = BaseUiEffectContext<typeof changeDescription.mla> & {
  actionId: Action['publicId'];
  subEditorArgs: ThresholdSubEditorArgs | DataRouteSubEditorArgs;
  htmlElementId: ReturnType<typeof getMLAHtmlElementId>;
};

export type AggregationUiEffectContext = BaseUiEffectContext<
typeof changeDescription.aggregation
> & {
  actionId: Action['publicId'];
  subEditorArgs: ThresholdSubEditorArgs | DataRouteSubEditorArgs;
};

export type ExpressionUiEffectContext = BaseUiEffectContext<typeof changeDescription.expression> & {
  expressionPath: string;
  actionId: Action['publicId'];
};

export type MatrixValueUiEffectContext = BaseUiEffectContext<
typeof changeDescription.matrixValue
> & {
  actionId: Action['publicId'];
  htmlElementId?: ReturnType<typeof getMatrixValueHtmlElementId>;
};

export type InputConfigurationUiEffect = BaseUiEffectContext<
  typeof changeDescription.inputConfiguration>
& {
  actionId: Action['publicId'];
  htmlElementId?: ReturnType<typeof getNodeEditorFormHtmlElementId>;
  relatedHtmlElementIds?: ReturnType<typeof getNodeEditorFormHtmlElementId>[];
};

export type ServiceLocalSettingsUiEffect = BaseUiEffectContext<
  typeof changeDescription.serviceLocalSettings> & {
  actionId: Action['publicId'];
  htmlElementId: ReturnType<typeof getNodeEditorFormHtmlElementId>;
};

export type MatrixOptionUiEffectContext = BaseUiEffectContext<
  typeof changeDescription.matrixOption>
& {
  actionId: Action['publicId'];
  htmlElementId: ReturnType<typeof getNodeEditorFormHtmlElementId>;
};

export type OutputOptionsUiEffectContext = BaseUiEffectContext<
  typeof changeDescription.outputOptions>
& {
  actionId: Action['publicId'];
  htmlElementId?: ReturnType<typeof getNodeEditorFormHtmlElementId>;
  relatedHtmlElementIds?: ReturnType<typeof getNodeEditorFormHtmlElementId>[];
};

export type OutputJqSettingsUiEffectContext = BaseUiEffectContext<
  typeof changeDescription.outputJqSettings>
& {
  actionId: Action['publicId'];
  htmlElementId: ReturnType<typeof getNodeEditorFormHtmlElementId>;
};

export type SnippetUiEffectContext = BaseUiEffectContext<typeof changeDescription.snippet> & {
  /** The snippet's container node. Only present for linked snippets. */
  publicId?: Action['publicId'];
};

export type DatarouteUiEffect = BaseUiEffectContext<
  typeof changeDescription.dataroute>
& {
  actionId: Action['publicId'];
  htmlElementId: ReturnType<typeof getDatarouteHtmlElementId>;
};
export type GlobalSettingsUiEffect = BaseUiEffectContext<
typeof changeDescription.globalSettings
> & {
  actionId: Action['publicId'];
  htmlElementId?: ReturnType<typeof getNodeEditorFormHtmlElementId>;
  relatedHtmlElementIds?: ReturnType<typeof getNodeEditorFormHtmlElementId>[];
};

export type TagLocalSettingsUiEffect = BaseUiEffectContext<
typeof changeDescription.tagLocalSettings
> & {
  actionId: Action['publicId'];
  htmlElementId?: ReturnType<typeof getNodeEditorFormHtmlElementId>;
};

export type FailureHandlingModeUiEffect = BaseUiEffectContext<
  typeof changeDescription.failureHandlingMode
>;

export type SdkConfigUiEffectContext = BaseUiEffectContext<
  typeof changeDescription.sdkConfig
> & {
  plugin: string;
};

export type UiEffectContext =
  | ActionUiEffectContext
  | AggregationUiEffectContext
  | DatarouteUiEffect
  | ExpressionUiEffectContext
  | FailureHandlingModeUiEffect
  | GlobalSettingsUiEffect
  | GroupUiEffectContext
  | InputConfigurationUiEffect
  | MLAUiEffectContext
  | MatrixOptionUiEffectContext
  | MatrixValueUiEffectContext
  | OutputJqSettingsUiEffectContext
  | OutputOptionsUiEffectContext
  | SdkConfigUiEffectContext
  | ServiceLocalSettingsUiEffect
  | SnippetUiEffectContext
  | TagLocalSettingsUiEffect
  | ThresholdUiEffectContext;

export type UiEffectContextByTarget = {
  [changeDescription.action]: ActionUiEffectContext;
  [changeDescription.aggregation]: AggregationUiEffectContext;
  [changeDescription.dataroute]: DatarouteUiEffect;
  [changeDescription.expression]: ExpressionUiEffectContext;
  [changeDescription.failureHandlingMode]: FailureHandlingModeUiEffect;
  [changeDescription.globalSettings]: GlobalSettingsUiEffect;
  [changeDescription.group]: GroupUiEffectContext;
  [changeDescription.inputConfiguration]: InputConfigurationUiEffect;
  [changeDescription.matrixValue]: MatrixValueUiEffectContext;
  [changeDescription.matrixOption]: MatrixOptionUiEffectContext;
  [changeDescription.mla]: MLAUiEffectContext;
  [changeDescription.outputJqSettings]: OutputJqSettingsUiEffectContext;
  [changeDescription.outputOptions]: OutputOptionsUiEffectContext;
  [changeDescription.sdkConfig]: SdkConfigUiEffectContext;
  [changeDescription.serviceLocalSettings]: ServiceLocalSettingsUiEffect;
  [changeDescription.snippet]: SnippetUiEffectContext;
  [changeDescription.tagLocalSettings]: TagLocalSettingsUiEffect;
  [changeDescription.threshold]: ThresholdUiEffectContext;
};
export type WorkflowHistoryContext = {
  operation: Operation;
  uiEffectContext: UiEffectContext;
};

export type WorkflowHistoryEntry = WorkflowHistoryContext & {
  diff: WorkflowDiff;
};

/** A callback that mutates redux state by performing its own dispatch(es). */
export type WorkflowStateChange = () => void;

export type EditorMode = 'diffy' | SnippetMode | null;
